import EditIcon from '@mui/icons-material/Edit'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material'
import { NodeViewWrapper } from '@tiptap/react'
import React, { useState } from 'react'

/**
 * 从 YouTube URL 中提取视频 ID 并转换为嵌入 URL
 * 支持多种 YouTube URL 格式：
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 * - https://www.youtube-nocookie.com/embed/VIDEO_ID
 */
export const getYouTubeEmbedUrl = (videoUrl) => {
  if (!videoUrl) return null

  try {
    // 如果已经是嵌入 URL，直接返回
    if (videoUrl.includes('/embed/')) {
      return videoUrl
    }

    let videoId = null

    // 处理 youtu.be 短链接
    if (videoUrl.includes('youtu.be/')) {
      videoId = videoUrl.split('youtu.be/')[1]?.split('?')[0]?.split('&')[0]
    }
    // 处理标准 YouTube URL
    else if (videoUrl.includes('youtube.com')) {
      const urlObj = new URL(videoUrl)
      videoId = urlObj.searchParams.get('v') || urlObj.pathname.split('/').pop()
    }

    if (videoId) {
      return `https://www.youtube-nocookie.com/embed/${videoId}`
    }

    return null
  } catch (error) {
    console.error('Error parsing YouTube URL:', error)
    return null
  }
}

/**
 * YouTube 视频节点视图组件
 * 显示 YouTube 视频 iframe，并提供编辑 URL 的功能
 */
const YouTubeNodeView = ({ node, updateAttributes, selected }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [url, setUrl] = useState('')
  const [error, setError] = useState('')

  /**
   * 从嵌入 URL 中提取视频 ID 并转换为标准 YouTube URL
   * 用于编辑时显示原始 URL
   */
  const getOriginalUrl = (embedUrl) => {
    if (!embedUrl) return ''

    try {
      // 如果是嵌入 URL，提取视频 ID
      if (embedUrl.includes('/embed/')) {
        const videoId = embedUrl.split('/embed/')[1]?.split('?')[0]?.split('&')[0]
        if (videoId) {
          return `https://www.youtube.com/watch?v=${videoId}`
        }
      }

      // 如果已经是标准 URL，直接返回
      return embedUrl
    } catch (error) {
      console.error('Error converting embed URL:', error)
      return embedUrl
    }
  }

  /**
   * 处理保存 URL
   */
  const handleSave = () => {
    setError('')
    const embedUrl = getYouTubeEmbedUrl(url)

    if (!embedUrl) {
      setError('Please enter a valid YouTube video URL')
      return
    }

    updateAttributes({
      src: embedUrl,
    })
    setIsEditing(false)
  }

  /**
   * 处理取消编辑
   */
  const handleCancel = () => {
    setUrl('')
    setError('')
    setIsEditing(false)
  }

  const embedUrl = node.attrs.src ? getYouTubeEmbedUrl(node.attrs.src) : null

  return (
    <NodeViewWrapper
      className={`youtube-node ${selected ? 'selected' : ''}`}
      style={{
        margin: '16px 0',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingTop: '56.25%', // 16:9 宽高比
          backgroundColor: 'action.hover',
          borderRadius: 1,
          overflow: 'hidden',
          border: selected ? '2px solid' : '1px solid',
          borderColor: selected ? 'primary.main' : 'divider',
        }}
      >
        {embedUrl ? (
          <iframe
            src={embedUrl}
            title="YouTube video player"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
              YouTube video URL not set
            </Typography>
            <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setIsEditing(true)} sx={{ mt: 1 }}>
              Set Video URL
            </Button>
          </Box>
        )}

        {/* 编辑按钮 - 悬停时显示 */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            transition: 'opacity 0.2s',
            opacity: 1, // Always show edit button
            // opacity: selected ? 1 : 0,
            // '&:hover': {
            //   opacity: 1,
            // },
          }}
        >
          <Button
            size="small"
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              setUrl(getOriginalUrl(node.attrs.src || ''))
              setIsEditing(true)
            }}
            sx={{
              minWidth: 'auto',
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              boxShadow: 2,
            }}
          >
            Change Video URL
          </Button>
        </Box>
      </Box>

      {/* 编辑对话框 */}
      <Dialog open={isEditing} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Edit YouTube Video URL</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="YouTube Video URL"
            fullWidth
            variant="outlined"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value)
              setError('')
            }}
            error={!!error}
            helperText={error || 'Supports standard YouTube URL or youtu.be short link'}
            placeholder="https://www.youtube.com/watch?v=VIDEO_ID or https://youtu.be/VIDEO_ID"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </NodeViewWrapper>
  )
}

export default YouTubeNodeView
